interface NamedCatalogItem {
  name: string;
}

export interface CatalogPreviewOption<T extends NamedCatalogItem> {
  kind: 'existing' | 'create';
  label: string;
  name: string;
  value: string;
  entity?: T;
}

export function normalizeCatalogPreviewName(value?: string | null) {
  return (value || '')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

export function createCatalogPreviewOptions<T extends NamedCatalogItem>(
  suggestedName: string,
  documents: T[],
  createLabel: (name: string) => string,
): CatalogPreviewOption<T>[] {
  const normalizedSuggestion = normalizeCatalogPreviewName(suggestedName);
  const existingOptions = documents.map((document) => ({
    kind: 'existing' as const,
    label: document.name,
    name: document.name,
    value: `existing:${normalizeCatalogPreviewName(document.name)}`,
    entity: document,
  }));

  if (!normalizedSuggestion) {
    return existingOptions;
  }

  const hasExactMatch = existingOptions.some(
    (option) => normalizeCatalogPreviewName(option.name) === normalizedSuggestion,
  );

  if (hasExactMatch) {
    return existingOptions;
  }

  return [
    {
      kind: 'create',
      label: createLabel(suggestedName),
      name: suggestedName.trim(),
      value: `create:${normalizedSuggestion}`,
    },
    ...existingOptions,
  ];
}

export function getInitialCatalogPreviewOption<T extends NamedCatalogItem>(
  suggestedName: string,
  options: CatalogPreviewOption<T>[],
) {
  const normalizedSuggestion = normalizeCatalogPreviewName(suggestedName);

  return (
    options.find((option) => {
      if (option.kind === 'create') {
        return normalizeCatalogPreviewName(option.name) === normalizedSuggestion;
      }

      return normalizeCatalogPreviewName(option.name) === normalizedSuggestion;
    }) || options[0] || null
  );
}
